{
  /* Generated by Copilot- Start - 2026-03-26 19:33:47 */
}
import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../video-lab/dtv-text";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import CardMedia from "@mui/material/CardMedia";
import IconBannerV2 from "./IconBannerV2/IconBannerV2";
import SvodBannerV2 from "./SvodBannerV2";
import { mainComponentStyles } from "../styles/component-styles";
import { IChnlInteractiveThemeUI } from "../types";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";
import { ZipcodeModuleProps } from "src/components/video-lab/zipcode-module";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

export interface SearchResultBannerProps {
  // Dimensions
  rowsInDesktop?: number;
  rowsInTablet?: number;
  rowsInMobile?: number;
  maxHeightDesktop?: number;
  maxHeightTablet?: number;
  maxHeightMobile?: number;

  // Theme & config
  theme?: IChnlInteractiveThemeUI;
  enableSvods?: boolean;
  svodTooltipContent?: string;
  openSvodModal?: boolean;
  pkgModalContent?: any;
  chnlSrc?: string;
  genreEnabled?: boolean;
  openChannelModal?: boolean;
  zipCodeModule?: ZipcodeModuleProps["ciConfig"];
  myHomeTeamModalCta?: { label: string; targeturl: string };
  tooltips?: any;

  // Channel/SVOD data
  channelMap?: Record<string, ChannelCardV2>;
  allCategories?: any[];

  // Search result data
  selectedShows: any[];
  selectedSvods: any[];
  selectedChannels: any[];
  searchedCategories: any[];
  searchedChannelsByCategory: Record<string, any>;
  searchedAddonCategories: any[];
  searchedAddonChannelsByCategory: Record<string, any>;

  // Handlers
  onChannelClick: (channel: ChannelCardV2) => void;
  onShowClick: (show: any) => void;

  // Show/category state
  showMoreCategories: Record<string, boolean>;
  setShowMoreCategories: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

  // Zipcode props (for MYHOMETEAM inline module)
  myhometeamZipcodeProps: {
    onZipcodeSearch: (...args: any[]) => void;
    onLocalRsnApiError: (...args: any[]) => void;
    onLocalRsnChnlsConfirmed: (...args: any[]) => void;
    onCacheConfirmed: (...args: any[]) => void;
    localRsnApiOptions: { satellite: boolean; stream: boolean };
    zipcodeInitData: any;
  };
}

/**
 * SearchResultBanner
 *
 * Renders the two search-driven result regions:
 *  1. Shows / Movies Grid   â€” visible when selectedShows.length > 0
 *  2. Channels & SVODs Grid â€” visible when selectedSvods or selectedChannels are present
 *
 * The regular (non-search) channel banner stays in the parent component.
 */
const SearchResultBanner: React.FC<SearchResultBannerProps> = ({
  rowsInDesktop,
  rowsInTablet,
  rowsInMobile,
  maxHeightDesktop,
  maxHeightTablet,
  maxHeightMobile,
  theme,
  enableSvods,
  svodTooltipContent,
  openSvodModal,
  pkgModalContent,
  chnlSrc,
  genreEnabled,
  openChannelModal,
  zipCodeModule,
  myHomeTeamModalCta,
  tooltips,
  channelMap,
  allCategories,
  selectedShows,
  selectedSvods,
  selectedChannels,
  searchedCategories,
  searchedChannelsByCategory,
  searchedAddonCategories,
  searchedAddonChannelsByCategory,
  onChannelClick,
  onShowClick,
  showMoreCategories,
  setShowMoreCategories,
  myhometeamZipcodeProps
}) => {
  const scrollContainerSx = {
    ...mainComponentStyles.showMoreContainerUtility,
    position: "relative",
    display: "block",
    overflowY: "scroll",
    minHeight: {
      xs: "26px",
      sm: "26px",
      md: "26px"
    },
    maxHeight: {
      xs: maxHeightMobile || (rowsInMobile ? `calc(24.8px + 48px + (78.3px * ${rowsInMobile}))` : "307.7px"),
      sm: maxHeightTablet || (rowsInTablet ? `calc(30px + 21.6px + (105.3px * ${rowsInTablet}))` : "367.5px"),
      md: maxHeightDesktop || (rowsInDesktop ? `calc(30px + 42px + (109.3px * ${rowsInDesktop}))` : "399.9px")
    },
    transition: "height ease 0.3s, margin-top ease 0.3s"
  };

  const innerIconContainerSx = {
    padding: 0,
    margin: 0,
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    pl: "5px",
    color: theme?.headerTextColor || colorPalette.neutral_white.main
  };

  return (
    <>
      {/* Search Results UI - Shows/Movies Grid */}
      {selectedShows?.length > 0 ? (
        <DtvBox sx={mainComponentStyles.showGrid}>
          {selectedShows.map(
            (show, index) => show?.images &&
              show?.images[0]?.imageUrl && (
                <DtvBox
                  key={index}
                  sx={{
                    ...mainComponentStyles.showItem,
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                      transform: "scale(1.02)",
                      transition: "all 0.2s ease-in-out"
                    }
                  }}
                  onClick={() => onShowClick(show)}
                  {...getAnalyticsAttrs(show?.title, 'panel_channel lineup', show?.title ? `/modal_show_${show.title}` : undefined)}
                >
                  <DtvBox sx={mainComponentStyles.showImage}>
                    <CardMedia
                      alt={show?.title || "show poster"}
                      component="img"
                      key={show?.resourceId || show?.images[0]?.imageUrl}
                      loading="lazy"
                      src={show?.images[0]?.imageUrl || show?.images[0]?.defaultImageUrl}
                      sx={mainComponentStyles.cardMedia}
                      onError={e => {
                        const fallback = show?.images[0]?.defaultImageUrl;
                        // Guard against infinite onError loop when both URLs fail
                        if (fallback && e.currentTarget.src !== fallback) {
                          e.currentTarget.src = fallback;
                        } else {
                          e.currentTarget.style.display = "none";
                        }
                      }}
                    />
                    <DtvText
                      content={show?.title}
                      sx={{
                        ...mainComponentStyles.showTitle,
                        color: theme?.subheadingTextColor || colorPalette.neutral_white.main
                      }}
                    />
                  </DtvBox>
                </DtvBox>
              )
          )}
        </DtvBox>
      ) : null}

      {/* Search Results UI - Channels and SVODs */}
      {selectedSvods?.length > 0 || selectedChannels?.length > 0 ? (
        <DtvBox id="search-results-container" sx={scrollContainerSx}>
          <DtvBox id="search-results-icons-container" sx={innerIconContainerSx}>
            {/* SVODs from search */}
            {enableSvods && selectedSvods && selectedSvods.length > 0 && (
              <SvodBannerV2
                chnlSrc={chnlSrc}
                openSvodModal={openSvodModal}
                pkgModalContent={pkgModalContent}
                svods={selectedSvods}
                theme={theme}
                tooltipContent={svodTooltipContent}
              />
            )}

            {/* Channels from search - includes BOTH regular and addon channels */}
            {selectedChannels?.length > 0 && (
              <>
                {/* Regular Channels (non-addon) */}
                {searchedCategories?.length > 0 && (
                  <IconBannerV2
                    addOnChannelsByCategory={{}}
                    allCategories={allCategories || []}
                    categories={searchedCategories}
                    channelMap={channelMap}
                    channelsByCategory={searchedChannelsByCategory}
                    genreEnabled={genreEnabled}
                    isSearchData={true}
                    myHomeTeamModalCta={myHomeTeamModalCta}
                    openChannelModal={openChannelModal}
                    showMoreData={showMoreCategories}
                    theme={theme}
                    tooltips={tooltips}
                    updateShowMoreCategories={setShowMoreCategories}
                    zipCodeModule={zipCodeModule}
                    onChannelClick={onChannelClick}
                    {...myhometeamZipcodeProps}
                  />
                )}

                {/* ADD-ON CHANNELS Section - matches v1 styling */}
                {searchedAddonCategories?.length > 0 && (
                  <DtvBox
                    id="ChnlListingPage-add-on-channels"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      flexWrap: "wrap",
                      padding: {
                        xs: "24px 14px",
                        sm: "11px 31px",
                        md: "48px 50px"
                      },
                      width: "100%",
                      backgroundColor: "#21528C !important",
                      order: 99,
                      overflow: "hidden",
                      mt: searchedCategories?.length > 0 ? "42px" : "0px"
                    }}
                  >
                    {/* Addon Header */}
                    <DtvBox sx={{
 display: "flex",
flexDirection: "column",
width: "100%"
}}>
                      <DtvText
                        content="ADD-ON CHANNELS"
                        variant="subhead1medium"
                        sx={{
                          fontSize: {
 xs: "16px",
md: "24px"
},
                          color: colorPalette.neutral_white.main,
                          lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "28.8px"
},
                          letterSpacing: {
 xs: "initial",
md: "-0.48px"
},
                          fontWeight: 400,
                          paddingBottom: {
 xs: "8px",
sm: "10px",
md: "12px"
}
                        }}
                      />
                      <DtvText
                        content="Available for an additional cost."
                        variant="body1"
                        sx={{
                          fontSize: {
 xs: "16px",
sm: "14px",
md: "20px"
},
                          color: colorPalette.neutral_white.main,
                          lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "26px"
},
                          fontWeight: {
 xs: 400,
sm: 300,
md: 400
}
                        }}
                      />
                    </DtvBox>

                    {/* Addon Channels Icons - pass isSearchData=false to render inline */}
                    <DtvBox sx={{ width: "100%" }}>
                      <IconBannerV2
                        addOnChannelsByCategory={{}}
                        allCategories={allCategories || []}
                        categories={searchedAddonCategories}
                        channelMap={channelMap}
                        channelsByCategory={searchedAddonChannelsByCategory}
                        genreEnabled={genreEnabled}
                        isSearchData={false}
                        myHomeTeamModalCta={myHomeTeamModalCta}
                        openChannelModal={openChannelModal}
                        showMoreData={showMoreCategories}
                        theme={theme}
                        tooltips={tooltips}
                        updateShowMoreCategories={setShowMoreCategories}
                        zipCodeModule={zipCodeModule}
                        onChannelClick={onChannelClick}
                        {...myhometeamZipcodeProps}
                      />
                    </DtvBox>
                  </DtvBox>
                )}
              </>
            )}
          </DtvBox>
        </DtvBox>
      ) : null}
    </>
  );
};

export default SearchResultBanner;
{
  /* Generated by Copilot- End - 2026-03-26 19:33:47 */
}
